import { QueryClientProvider, type QueryClient } from "@tanstack/react-query";
import { LinkProvider, ToastProvider, TooltipProvider } from "@sarva/ui";
import { ActionErrorToaster } from "@/shared/ActionErrors";
import { RouterUiLink } from "@/shared/layout/RouterUiLink";
import { useState, type ReactNode } from "react";
import { createQueryClient } from "./query-client";
import { ThemeProvider } from "./theme";
import { AppErrorBoundary } from "./error-boundary";
import { ShortcutRegistryProvider } from "@/shared/shortcuts";

/**
 * One place where every cross-cutting provider is mounted, in a fixed order:
 * error boundary outermost (so a provider that throws is still caught), then
 * density, then server state, then the UI-level providers (tooltips, toasts,
 * the shortcut registry).
 *
 * (H) doc 40 §15: there is no theme provider in that list. Dark is deferred.
 *
 * **What is deliberately not here any more:** `<PermissionProvider>` and
 * `<TimeZoneProvider>`. Both used to be fed from a mock session at this level,
 * which meant they were available on the sign-in screen — before there was an
 * identity to derive them from. They now live inside `<SessionProvider>`, which
 * mounts only behind `<RequireAuth>` and takes its values from `GET /me`. A
 * `<PermissionGate>` rendered outside a session now throws instead of silently
 * answering from a default, which is the correct failure: (H) doc 27 §7 makes
 * the API the authority, and a gate with no identity has no authority to quote.
 *
 * Density defaults rather than being read from the session: the API carries no
 * user preferences yet, and the shell's own control writes it for the session.
 */
export interface AppProvidersProps {
  children: ReactNode;
  /**
   * Supply the query client instead of letting the application build one.
   *
   * The default is `createQueryClient()` and production always uses it. The
   * seam exists so a test can run without the retry policy — a `429` or a `5xx`
   * is retried twice with a growing delay, which is right in a browser and
   * turns a three-line assertion into a four-second one in a test. The policy
   * itself is verified where it lives, in `query-client.ts`.
   */
  queryClient?: QueryClient;
}

export function AppProviders({ children, queryClient }: AppProvidersProps) {
  const [fallbackClient] = useState(createQueryClient);
  const client = queryClient ?? fallbackClient;

  return (
    <AppErrorBoundary>
      <ThemeProvider initialDensity="compact">
        <QueryClientProvider client={client}>
          <TooltipProvider delayDuration={300}>
            <ToastProvider>
              <ActionErrorToaster />
              <LinkProvider component={RouterUiLink}>
                <ShortcutRegistryProvider>{children}</ShortcutRegistryProvider>
              </LinkProvider>
            </ToastProvider>
          </TooltipProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </AppErrorBoundary>
  );
}
